import { lazy, ComponentType, LazyExoticComponent } from "react";

/**
 * Handles the classic "open tab breaks after a deploy" problem.
 *
 * When we ship a new frontend build, Firebase Hosting publishes new
 * content-hashed JS/CSS chunks and removes the previous build's files. A tab
 * that was loaded before the deploy still references the OLD chunk URLs, so the
 * next code-split import (a route, the login screen, etc.) 404s and throws a
 * ChunkLoadError.
 *
 * The fix used by most SPA teams: when a dynamic import fails because the chunk
 * is gone, do a single full-page reload to pull the fresh index.html + new
 * hashes. We guard with sessionStorage so a genuinely broken build can't trigger
 * an infinite reload loop, and we clear the guard on any successful import so a
 * later deploy can recover again.
 *
 * Crucially this must NEVER be treated as an auth/"stale session" problem — a
 * chunk 404 has nothing to do with the user's tokens, so we never clear them.
 */
const CHUNK_RELOAD_KEY = "app:chunkReloadAttempted";

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as { name?: string }).name ?? "";
  const message = (error as { message?: string }).message ?? "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk [\w-]+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  );
}

/** Clear the reload guard after a chunk loads successfully. */
export function markChunkLoadSuccess(): void {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // sessionStorage may be unavailable (privacy mode); nothing to clear.
  }
}

/**
 * Trigger a one-time hard reload to fetch fresh assets. Returns `true` if a
 * reload was started (caller should stop and let the navigation happen), or
 * `false` if we already reloaded once this session (avoid a reload loop).
 */
export function reloadOnceForChunkError(): boolean {
  try {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_KEY) === "true") {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");
  } catch {
    // If sessionStorage is unavailable we can't loop-guard; reload best-effort.
  }
  window.location.reload();
  return true;
}

/**
 * Drop-in replacement for `React.lazy` that recovers from post-deploy chunk
 * failures by reloading once for fresh assets. Mirrors React.lazy's own generic
 * signature so component prop types are preserved.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory();
      markChunkLoadSuccess();
      return mod;
    } catch (error) {
      if (isChunkLoadError(error) && reloadOnceForChunkError()) {
        // A reload is underway; never resolve so React doesn't flash an error
        // boundary before the page navigates away.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}
